/*  Greatly Social – ImageEditorModal  (v2.0)
    • API calls now go through /api/… so they work behind Apache reverse-proxy
------------------------------------------------------------------ */
import React, { useEffect, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import FilerobotImageEditor from 'react-filerobot-image-editor'

/* ───────── Decide backend root once ───────── */
const ENV_ROOT = process.env.REACT_APP_BACKEND_URL || ''
const API_BASE = ENV_ROOT ? `${ENV_ROOT}/api` : '/api'

const CDN_HOST = 'https://files.greatlysocial.com'   // CloudFront front door

/* data-URL ➜ File -------------------------------------------------- */
function dataURLtoFile (dataUrl, name) {
  const [meta, b64] = dataUrl.split(',')
  const mime  = meta.match(/:(.*?);/)[1]
  const bytes = atob(b64)
  const arr   = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
  return new File([arr], name, { type: mime })
}

/* presigned-POST upload ------------------------------------------- */
/* No userId: the server files it under the logged-in user's folder. */
async function uploadImage (file, prefix = 'edited') {
  /* 1️⃣ presign */
  const r = await fetch(
    `${API_BASE}/generate-presigned` +
    `?fileName=${encodeURIComponent(file.name)}` +
    `&prefix=${encodeURIComponent(prefix)}` +
    `&contentType=${encodeURIComponent(file.type)}`,
    { credentials: 'include' }
  )
  if (!r.ok) throw new Error('presign failed')
  const { url, fields, key } = await r.json()

  /* 2️⃣ form */
  const fd = new FormData()
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v))
  if (!('Content-Type' in fields)) fd.append('Content-Type', file.type)
  fd.append('file', file)

  /* 3️⃣ POST */
  const up = await fetch(url, { method: 'POST', body: fd })
  if (!up.ok) throw new Error('upload failed')

  return `${CDN_HOST}/${key}`
}

/* remote URL ➜ data-URL (handles path-style) ---------------------- */
async function urlToDataURL (url) {
  const m = url.match(/^https:\/\/s3\.[^.]+\.amazonaws\.com\/files\.greatlysocial\.com\/(.+)$/)
  if (m) url = `${CDN_HOST}/${m[1]}`
  const res = await fetch(url, { mode: 'cors' })
  if (!res.ok) throw new Error('fetch failed')
  const blob = await res.blob()
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.readAsDataURL(blob)
  })
}

export default function ImageEditorModal ({
  show = false,
  src = '',
  onClose = () => {},
  onComplete = () => {},
}) {
  const [source,  setSource]  = useState('')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)

  /* preload -------------------------------------------------------- */
  useEffect(() => {
    if (!show || !src) return
    if (src instanceof File || src.startsWith('data:')) {
      setSource(src); setError(null); return
    }
    setLoading(true)
    urlToDataURL(src)
      .then(d => { setSource(d); setError(null) })
      .catch(() => setError('Failed to load image'))
      .finally(() => setLoading(false))
  }, [show, src])

  if (!show) return null

  const handleSave = async ({ imageBase64 }) => {
    try {
      const file   = dataURLtoFile(imageBase64, `edited-${Date.now()}.png`)
      const httpsUrl = await uploadImage(file)
      onComplete({ url: httpsUrl, file })
    } catch (e) {
      console.error(e)
      alert('Failed to upload edited image')
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Body style={{ height: '80vh', padding: 0 }}>
        {loading && (
          <div className="d-flex h-100 w-100 justify-content-center align-items-center">
            <Spinner animation="border" />
          </div>
        )}

        {error && (
          <div className="d-flex h-100 w-100 justify-content-center align-items-center">
            <p className="text-danger">{error}</p>
          </div>
        )}

        {!loading && !error && source && (
          <FilerobotImageEditor
            source={source}
            /* ⛔ prevent built-in “Save As” modal */
            onBeforeSave={() => false}
            onSave={handleSave}
            onClose={onClose}
            showInModal={false}
            defaultSavedImageType="png"
            Crop={{
              defaultTab: 'crop',
              presetsItems: [
                { titleKey:'Square',       descriptionKey:'1:1',    ratio:1 },
                { titleKey:'IG Portrait',  descriptionKey:'4:5',    ratio:4/5 },
                { titleKey:'IG Landscape', descriptionKey:'1.91:1', ratio:1.91 },
                { titleKey:'Story / Reel', descriptionKey:'9:16',   ratio:9/16 },
                { titleKey:'X',            descriptionKey:'16:9',   ratio:16/9 },
                { titleKey:'Pinterest',    descriptionKey:'2:3',    ratio:2/3 },
              ],
            }}
          />
        )}
      </Modal.Body>
    </Modal>
  )
}
